import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'zite-auth-sdk';
import { getModuleDetail, submitQuiz, GetModuleDetailOutputType, SubmitQuizOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { CheckCircle, XCircle, ChevronLeft, Download, Award } from 'lucide-react';
import { buildModuleCertificateHtml, downloadCertificateFile } from '@/lib/complianceCertificate';

type Question = GetModuleDetailOutputType['standardQuestions'][0];
type Result = SubmitQuizOutputType;

export default function Quiz() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [moduleTitle, setModuleTitle] = useState('');
  const [categoryName, setCategoryName] = useState('');
  const [passMarkPercent, setPassMarkPercent] = useState(80);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<Result | null>(null);

  useEffect(() => {
    if (!user || !id) return;
    getModuleDetail({ moduleId: id }).then(d => {
      setQuestions(d.standardQuestions);
      setModuleTitle(d.module.title);
      setCategoryName(d.module.categoryName);
      setPassMarkPercent(d.module.passMarkPercent);
    }).finally(() => setLoading(false));
  }, [user, id]);

  const handleDownloadCertificate = () => {
    if (!result?.passed) return;
    const staffName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || user?.email || 'Team Member';
    const html = buildModuleCertificateHtml({
      staffName,
      moduleTitle,
      categoryName,
      quizScore: result.score,
      passMark: passMarkPercent,
      completedDate: new Date().toISOString(),
      role: user?.position || user?.role,
      department: user?.department,
    });
    const filename = `Certificate-${moduleTitle.replace(/\s+/g, '-')}-${staffName.replace(/\s+/g, '-')}.html`;
    downloadCertificateFile(html, filename);
  };

  const q = questions[current];
  const options = q ? [
    { key: 'A', text: q.optionA }, { key: 'B', text: q.optionB },
    { key: 'C', text: q.optionC }, { key: 'D', text: q.optionD },
  ].filter(o => o.text) : [];

  const selectAnswer = (key: string) => setSelected(key);

  const next = () => {
    if (!selected) return;
    const newAnswers = { ...answers, [q.id]: selected };
    setAnswers(newAnswers);
    setSelected(null);
    if (current + 1 < questions.length) {
      setCurrent(c => c + 1);
    } else {
      setSubmitting(true);
      submitQuiz({ moduleId: id!, answers: Object.entries(newAnswers).map(([questionId, answer]) => ({ questionId, answer })) })
        .then(setResult)
        .catch(console.error)
        .finally(() => setSubmitting(false));
    }
  };

  if (loading) return <div className="p-8"><Skeleton className="h-96 w-full" /></div>;

  if (questions.length === 0) return (
    <div className="p-8 text-center">
      <p className="text-muted-foreground">No quiz questions for this module yet.</p>
      <Button variant="outline" className="mt-4" onClick={() => navigate(`/modules/${id}`)}>← Back to module</Button>
    </div>
  );

  if (submitting) return (
    <div className="flex items-center justify-center p-16 text-center">
      <div>
        <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-muted-foreground">Submitting your answers...</p>
      </div>
    </div>
  );

  if (result) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-10">
        <div className={`p-8 mb-8 text-center border-2 rounded-xl ${result.passed ? 'border-primary bg-primary/5' : 'border-destructive/40 bg-destructive/5'}`}>
          {result.passed
            ? <CheckCircle className="w-12 h-12 text-primary mx-auto mb-3" />
            : <XCircle className="w-12 h-12 text-destructive mx-auto mb-3" />}
          <h2 className="text-2xl font-bold text-foreground mb-1">{result.passed ? 'Passed! 🎉' : 'Not quite'}</h2>
          <p className="text-4xl font-bold text-foreground mb-2">{result.score}%</p>
          <p className="text-muted-foreground">{result.correct}/{result.total} correct · Pass mark: {passMarkPercent}%</p>
          {result.passed ? (
            <p className="text-primary text-sm mt-3 font-medium">Your manager has been notified and will sign off your practical shortly.</p>
          ) : (
            <p className="text-muted-foreground text-sm mt-3">Review the answers below and retake when ready.</p>
          )}

          {/* Certificate download — shown immediately on pass */}
          {result.passed && (
            <div className="mt-6 pt-5 border-t border-primary/20">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Award className="w-5 h-5 text-primary" />
                <p className="text-sm font-semibold text-foreground">Your certificate is ready</p>
              </div>
              <p className="text-xs text-muted-foreground mb-4">
                Download your Certificate of Achievement. Open it in your browser and use <strong>File → Print → Save as PDF</strong> for a permanent copy.
              </p>
              <Button onClick={handleDownloadCertificate} className="gap-2 bg-primary text-primary-foreground">
                <Download className="w-4 h-4" /> Download Certificate
              </Button>
            </div>
          )}
        </div>

        <div className="space-y-4 mb-8">
          {result.results.map((r, i) => (
            <div key={r.questionId} className={`border p-4 rounded-lg ${r.correct ? 'border-primary/20 bg-primary/5' : 'border-destructive/20 bg-destructive/5'}`}>
              <div className="flex items-start gap-2 mb-2">
                {r.correct ? <CheckCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />}
                <p className="text-sm font-medium text-foreground">{i + 1}. {r.question}</p>
              </div>
              {!r.correct && (
                <p className="text-xs text-muted-foreground ml-6">Your answer: {r.yourAnswer} · Correct: {r.correctAnswer}</p>
              )}
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-3">
          {!result.passed && (
            <Button className="bg-primary text-primary-foreground" onClick={() => { setResult(null); setCurrent(0); setAnswers({}); setSelected(null); }}>
              Retake quiz
            </Button>
          )}
          <Button variant="outline" onClick={() => navigate(`/modules/${id}`)}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Back to module
          </Button>
          {result.passed && (
            <Button variant="outline" onClick={() => navigate('/')}>Back to My Journey</Button>
          )}
          {result.passed && (
            <Button variant="outline" onClick={handleDownloadCertificate} className="gap-2">
              <Download className="w-4 h-4" /> Download Certificate
            </Button>
          )}
        </div>
      </div>
    );
  }

  const progress = ((current) / questions.length) * 100;

  return (
    <div className="max-w-2xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="mb-8">
        <p className="text-xs text-muted-foreground uppercase tracking-widest mb-1">{moduleTitle}</p>
        <div className="flex items-center justify-between mb-3">
          <p className="font-semibold text-foreground">Question {current + 1} of {questions.length}</p>
        </div>
        <div className="h-1 bg-muted w-full rounded-full">
          <div className="h-1 bg-primary transition-all rounded-full" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Question */}
      <p className="text-lg font-medium text-foreground mb-6 leading-relaxed">{q.question}</p>
      {q.questionImageUrl && (
        <img src={q.questionImageUrl} alt="Question" className="mb-6 max-h-48 object-contain border border-border rounded" />
      )}

      {/* Options */}
      <div className="flex flex-col gap-3 mb-8">
        {options.map(opt => (
          <button
            key={opt.key}
            onClick={() => selectAnswer(opt.key!)}
            className={`px-5 py-4 text-left text-sm flex items-start gap-3 border rounded-lg transition-colors ${
              selected === opt.key
                ? 'border-primary bg-primary/10 text-foreground'
                : 'border-border bg-card text-foreground hover:border-primary/40'
            }`}
          >
            <span className={`w-6 h-6 flex items-center justify-center text-xs font-bold shrink-0 border rounded ${selected === opt.key ? 'bg-primary text-primary-foreground border-primary' : 'border-muted-foreground'}`}>
              {opt.key}
            </span>
            {opt.text}
          </button>
        ))}
      </div>

      <Button
        onClick={next}
        disabled={!selected}
        className="bg-primary text-primary-foreground px-8"
      >
        {current + 1 === questions.length ? 'Submit Quiz' : 'Next question →'}
      </Button>
    </div>
  );
}
